import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Box} from '@dropbox/dig-foundations';
import {snackbarAtom} from 'atoms/snackbar';
import {Workstream, WorkstreamEmployeeEdit} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useState} from 'react';
import type {WorkstreamCustomPrivacyConfig} from 'views/workstreams/detail/WorkstreamDetailsSidebar';
import {WorkstreamPrivacyDrawer} from 'views/workstreams/WorkstreamPrivacyDrawer';

/**
 * /workstreams detail: the properties row's privacy "Custom" option. The old
 * page's custom-audience drawer (same picker, same save) as a modal, since this
 * page has no side drawers. Owners show as always able to see it.
 */
export const CustomPrivacyModal = ({
  workstream,
  config,
  onClose,
}: {
  workstream: Workstream;
  config: WorkstreamCustomPrivacyConfig;
  onClose: () => void;
}) => {
  const setSnackbar = useSetAtom(snackbarAtom);
  const [employees, setEmployees] = useState(config.selectedEmployees);
  const [teams, setTeams] = useState(config.selectedTeams);
  const [isSaving, setIsSaving] = useState(false);

  const owners = [
    ...workstream.employee_associations.map(({employee: {user_id}, dri}) => ({dri, user_id})),
    ...(workstream.metrics
      .map(({owner}) => ({dri: 'outcome_dri', user_id: owner?.user_id}))
      .filter(({user_id}) => Boolean(user_id)) as WorkstreamEmployeeEdit[]),
  ] satisfies WorkstreamEmployeeEdit[];

  const close = () => {
    if (!isSaving) onClose();
  };

  const save = async () => {
    setIsSaving(true);
    try {
      await config.onSave(employees, teams);
      setSnackbar({text: t('saved')});
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isCentered open withCloseButton="Close" onRequestClose={close} width={560}>
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title weightVariant="emphasized">{t('privacy')}</Modal.Title>
      </Modal.Header>
      {/* Not Modal.Body: its override (dig-overrides/modal.css) zeroes the search
          row's bottom margin, which the picker's suggestion chips pull up into. */}
      <Box padding="24" style={{flexGrow: 1, overflowY: 'auto'}}>
        <WorkstreamPrivacyDrawer
          owners={owners}
          selectedEmployees={employees}
          selectedTeams={teams}
          setSelectedEmployees={setEmployees}
          setSelectedTeams={setTeams}
        />
      </Box>
      <Modal.Footer>
        <CancelButton onClick={close} disabled={isSaving}>
          {t('cancel')}
        </CancelButton>
        <Button variant="primary" onClick={() => void save()} disabled={isSaving}>
          {t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
